import { useCallback, useEffect, useRef } from 'react';
export function useLiveKitCloudsDataChannel({ room, publishReliableData, onCloudsOpen, onCloudsClose, onCloudsDrawLine, onCloudsClearDrawing, onCloudsSelectImage, onCloudsClientFinished, onCloudsUndo, onCloudsHideVideoForClient, }) {
    const pendingCloudsLinesRef = useRef([]);
    const cloudsLinesFlushTimerRef = useRef(null);
    useEffect(() => {
        return () => {
            if (cloudsLinesFlushTimerRef.current) {
                clearTimeout(cloudsLinesFlushTimerRef.current);
                cloudsLinesFlushTimerRef.current = null;
            }
            pendingCloudsLinesRef.current = [];
        };
    }, []);
    const sendCloudsOpen = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'clouds_open', from: room.localParticipant.identity };
        publishReliableData(message);
    }, [publishReliableData, room]);
    const sendCloudsClose = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'clouds_close', from: room.localParticipant.identity };
        publishReliableData(message);
    }, [publishReliableData, room]);
    // Clouds drawing - отправка линии
    const sendCloudsDrawLine = useCallback((line) => {
        if (!room)
            return;
        pendingCloudsLinesRef.current.push(line);
        if (cloudsLinesFlushTimerRef.current) {
            return;
        }
        cloudsLinesFlushTimerRef.current = setTimeout(() => {
            cloudsLinesFlushTimerRef.current = null;
            const lines = pendingCloudsLinesRef.current;
            pendingCloudsLinesRef.current = [];
            if (lines.length === 0) {
                return;
            }
            publishReliableData({
                type: 'clouds_draw_lines',
                from: room.localParticipant.identity,
                lines,
            });
        }, 16);
    }, [publishReliableData, room]);
    // Clouds drawing - очистка холста
    const sendCloudsClearDrawing = useCallback(() => {
        if (!room)
            return;
        if (cloudsLinesFlushTimerRef.current) {
            clearTimeout(cloudsLinesFlushTimerRef.current);
            cloudsLinesFlushTimerRef.current = null;
        }
        pendingCloudsLinesRef.current = [];
        const message = { type: 'clouds_clear_drawing', from: room.localParticipant.identity };
        publishReliableData(message);
    }, [publishReliableData, room]);
    // Clouds drawing - выбор изображения
    const sendCloudsSelectImage = useCallback((imageIndex) => {
        if (!room)
            return;
        if (cloudsLinesFlushTimerRef.current) {
            clearTimeout(cloudsLinesFlushTimerRef.current);
            cloudsLinesFlushTimerRef.current = null;
        }
        pendingCloudsLinesRef.current = [];
        const message = { type: 'clouds_select_image', from: room.localParticipant.identity, imageIndex };
        publishReliableData(message);
    }, [publishReliableData, room]);
    // Clouds drawing - клиент завершил рисование
    const sendCloudsClientFinished = useCallback(() => {
        if (!room)
            return;
        if (cloudsLinesFlushTimerRef.current) {
            clearTimeout(cloudsLinesFlushTimerRef.current);
            cloudsLinesFlushTimerRef.current = null;
        }
        const lines = pendingCloudsLinesRef.current;
        pendingCloudsLinesRef.current = [];
        if (lines.length > 0) {
            publishReliableData({
                type: 'clouds_draw_lines',
                from: room.localParticipant.identity,
                lines,
            });
        }
        const message = { type: 'clouds_client_finished', from: room.localParticipant.identity };
        publishReliableData(message);
    }, [publishReliableData, room]);
    // Clouds drawing - отмена последнего штриха
    const sendCloudsUndo = useCallback((linesCount) => {
        if (!room)
            return;
        if (cloudsLinesFlushTimerRef.current) {
            clearTimeout(cloudsLinesFlushTimerRef.current);
            cloudsLinesFlushTimerRef.current = null;
        }
        const lines = pendingCloudsLinesRef.current;
        pendingCloudsLinesRef.current = [];
        if (lines.length > 0) {
            publishReliableData({
                type: 'clouds_draw_lines',
                from: room.localParticipant.identity,
                lines,
            });
        }
        const message = { type: 'clouds_undo', from: room.localParticipant.identity, linesCount };
        publishReliableData(message);
    }, [publishReliableData, room]);
    // Clouds - скрытие видео для клиента
    const sendCloudsHideVideoForClient = useCallback((hide) => {
        if (!room)
            return;
        const message = { type: 'clouds_hide_video_for_client', from: room.localParticipant.identity, hide };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    const handleCloudsData = useCallback((parsed) => {
        if (parsed.type === 'clouds_open') {
            if (room.localParticipant.identity !== parsed.from) {
                onCloudsOpen?.();
            }
        }
        if (parsed.type === 'clouds_close') {
            if (room.localParticipant.identity !== parsed.from) {
                onCloudsClose?.();
            }
        }
        if (parsed.type === 'clouds_draw_line') {
            if (room.localParticipant.identity !== parsed.from && parsed.line) {
                onCloudsDrawLine?.(parsed.line);
            }
        }
        if (parsed.type === 'clouds_draw_lines') {
            if (room.localParticipant.identity !== parsed.from && Array.isArray(parsed.lines)) {
                parsed.lines.forEach((line) => {
                    onCloudsDrawLine?.(line);
                });
            }
        }
        if (parsed.type === 'clouds_clear_drawing') {
            if (room.localParticipant.identity !== parsed.from) {
                onCloudsClearDrawing?.();
            }
        }
        if (parsed.type === 'clouds_select_image') {
            if (room.localParticipant.identity !== parsed.from && typeof parsed.imageIndex === 'number') {
                onCloudsSelectImage?.(parsed.imageIndex);
            }
        }
        if (parsed.type === 'clouds_client_finished') {
            if (room.localParticipant.identity !== parsed.from) {
                onCloudsClientFinished?.();
            }
        }
        if (parsed.type === 'clouds_undo') {
            if (room.localParticipant.identity !== parsed.from && typeof parsed.linesCount === 'number') {
                onCloudsUndo?.(parsed.linesCount);
            }
        }
        if (parsed.type === 'clouds_hide_video_for_client') {
            if (room.localParticipant.identity !== parsed.from && typeof parsed.hide === 'boolean') {
                onCloudsHideVideoForClient?.(parsed.hide);
            }
        }
    }, [
        onCloudsClearDrawing,
        onCloudsClientFinished,
        onCloudsClose,
        onCloudsDrawLine,
        onCloudsHideVideoForClient,
        onCloudsOpen,
        onCloudsSelectImage,
        onCloudsUndo,
        room,
    ]);
    return {
        handleCloudsData,
        sendCloudsOpen,
        sendCloudsClose,
        sendCloudsDrawLine,
        sendCloudsClearDrawing,
        sendCloudsSelectImage,
        sendCloudsClientFinished,
        sendCloudsUndo,
        sendCloudsHideVideoForClient,
    };
}
